import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageType, SystemMessage } from '@connect-x-app/shared-domain-models';
import { SystemMessageWithType } from '@connect-x-app/shared-view-models';

import { useSystemMessages } from '../queries/useSystemMessages';
import { useUpdateSystemMessages } from '../queries/useUpdateSystemMessages';

const GeneralMessageTypes: MessageType[] = [
  MessageType.SYSTEM_GATE_UPDATE,
  MessageType.SYSTEM_CREW_CHANGE,
  MessageType.SYSTEM_AIRCRAFT_CHANGE,
];

const OFPMessageTypes: MessageType[] = [
  MessageType.SYSTEM_OFP_RELEASED,
  MessageType.SYSTEM_OFP_COMPLETED,
  MessageType.SYSTEM_FLIGHT_DELAY,
  MessageType.SYSTEM_FLIGHT_DELAY_NEW_ETA,
];

const TimeMessagePrefix = 'SYSTEM_TIME_UPDATE_';
const FFDMessagePrefix = 'SYSTEM_FFD_';
const LoadsheetMessagePrefix = 'SYSTEM_LOADSHEET_';
const AvioCastMessagePrefix = 'SYSTEM_AVIOCAST_';

export const useGroupedSystemMessages = () => {
  const { t } = useTranslation();

  const { data: systemMessages, isPending: isLoadingGetSystemMessages } = useSystemMessages();
  const { isPending: isLoadingUpdateSystemMessages, updateSystemMessages } = useUpdateSystemMessages();

  const dataWithMessageType = useMemo(() => {
    return (Object.entries(systemMessages ?? {}) as [MessageType, SystemMessage][]).map(([key, value]) => ({
      messageType: key,
      ...value,
    }));
  }, [systemMessages]);

  const groupedMessages = useMemo(() => {
    return dataWithMessageType.reduce<Record<string, SystemMessageWithType[]>>((gMessages, systemMessage) => {
      const fallbackCategory = t('SYSTEM_MESSAGES.SUBTITLES.OTHER');

      const categoryMatchers = [
        {
          category: t('SYSTEM_MESSAGES.SUBTITLES.GENERAL'),
          matches: (type: MessageType) => GeneralMessageTypes.includes(type),
        },
        {
          category: t('SYSTEM_MESSAGES.SUBTITLES.TIME_CHANGE'),
          matches: (type: MessageType) => type.startsWith(TimeMessagePrefix),
        },
        {
          category: t('SYSTEM_MESSAGES.SUBTITLES.OFP'),
          matches: (type: MessageType) => OFPMessageTypes.includes(type),
        },
        {
          category: t('SYSTEM_MESSAGES.SUBTITLES.FFD'),
          matches: (type: MessageType) => type.startsWith(FFDMessagePrefix),
        },
        {
          category: t('SYSTEM_MESSAGES.SUBTITLES.LOADSHEET'),
          matches: (type: MessageType) => type.startsWith(LoadsheetMessagePrefix),
        },
        {
          category: t('SYSTEM_MESSAGES.SUBTITLES.AVIOCAST'),
          matches: (type: MessageType) => type.startsWith(AvioCastMessagePrefix),
        },
      ];

      const matchedCategory = categoryMatchers.find(matcher => matcher.matches(systemMessage.messageType));

      const category = matchedCategory ? matchedCategory.category : fallbackCategory;

      if (!gMessages[category]) {
        gMessages[category] = [];
      }
      gMessages[category].push(systemMessage);
      return gMessages;
    }, {});
  }, [dataWithMessageType, t]);

  return {
    groupedMessages,
    isLoadingGetSystemMessages,
    isLoadingUpdateSystemMessages,
    systemMessages,
    updateSystemMessages,
  };
};
